/**
 * Eén plek voor het versturen van mails via de backend (Microsoft Graph).
 *
 * Mail versturen is kritiek: de gebruiker moet altijd weten of zijn mail
 * vertrokken is, en zo niet, waar het misliep. Voordien werd een mislukking
 * nergens getoond — het verzoek gebeurde in een callback waarvan de fout
 * verdween, en het mailvenster bleef gewoon openstaan. Gebruikers klikten dan
 * opnieuw (waargenomen: 7 pogingen voor 1 verstuurde mail).
 *
 * Deze functie gooit ALTIJD een fout wanneer er niets verstuurd is, met een
 * boodschap die aan de gebruiker getoond mag worden.
 */

/** Fasen die de backend onderscheidt, zodat we kunnen zeggen wáár het misliep. */
export type MailSendStage = 'attachments' | 'auth' | 'graph' | 'sender' | 'unknown';

export class MailSendFailure extends Error {
  constructor(public stage: MailSendStage, message: string) {
    super(message);
    this.name = 'MailSendFailure';
  }
}

const STAGE_UITLEG: Record<MailSendStage, string> = {
  attachments: 'De bijlagen konden niet opgehaald worden.',
  auth: 'Er kon geen verbinding gemaakt worden met Microsoft.',
  graph: 'Microsoft heeft de mail geweigerd.',
  sender: 'Dit afzenderadres mag hier niet versturen.',
  unknown: 'Er ging iets mis bij het versturen.',
};

/** Antwoord van de backend na een geslaagde verzending. */
export interface MailSendResult {
  sent: true;
  [key: string]: unknown;
}

export interface SendMailPayload {
  from?: string;
  to?: string;
  cc?: string;
  bcc?: string;
  subject?: string;
  body?: string;
  attachments?: string;
  construction_site_id?: number;
  purchase_order_id?: number;
  billit_invoice_id?: number;
  customer_invoice_id?: number;
  customer_contract_update_id?: number;
  execution_list_item_id?: number;
  [key: string]: unknown;
}

/**
 * Verstuurt de mail en wacht op het resultaat.
 * @throws {MailSendFailure} wanneer de mail NIET verstuurd is.
 */
export const sendMailRequest = async (
  token: string,
  payload: SendMailPayload,
): Promise<MailSendResult> => {
  let response: Response;

  try {
    response = await fetch(`${process.env.REACT_APP_BACKEND_URI}/mails/send-email`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    // Netwerkfout: het verzoek is de browser niet uit geraakt.
    throw new MailSendFailure(
      'unknown',
      'De server is niet bereikbaar. De mail is NIET verstuurd.',
    );
  }

  if (!response.ok) {
    let stage: MailSendStage = 'unknown';
    let bericht = '';

    try {
      const data = await response.json();
      stage = (data?.stage as MailSendStage) || 'unknown';
      bericht = data?.error || '';
    } catch {
      // geen leesbaar antwoord; val terug op de algemene uitleg
    }

    throw new MailSendFailure(
      stage,
      bericht || `${STAGE_UITLEG[stage]} De mail is NIET verstuurd.`,
    );
  }

  return response.json();
};

export default sendMailRequest;
